import { Link } from "react-router-dom";
import useSiteContent from "@/hooks/useSiteContent";

const DEFAULT_PARAS = [
  "I am Pankaj Talan, a Chartered Accountant and an SRCC graduate with experience across corporate finance, financial reporting, internal controls, audit coordination, regulatory reporting, and business process consulting.",
  "Over the years, I have worked with complex financial reporting environments and gained exposure to governance, compliance, risk management, and finance operations. My experience includes working with one of the world's leading investment management firms, where accuracy, strong controls, and high-quality reporting were non-negotiable.",
  "Today, I help startups, SMEs, family businesses, and growing organizations build finance functions that go beyond compliance. My focus is on creating systems and processes that enable businesses to scale confidently.",
  "I believe a Chartered Accountant should not only ensure statutory compliance but also act as a strategic business partner. Whether it is implementing SOPs, designing internal financial controls, improving reporting systems, assisting with fundraising, or acting as a Virtual CFO — my objective is to help businesses make informed financial decisions and build sustainable growth.",
];

export default function About() {
  const cms = useSiteContent();
  const about = cms.about || {};
  const paragraphs = about.paragraphs?.length ? about.paragraphs : DEFAULT_PARAS;
  const quote = about.quote || "At our firm, we don't just prepare reports — we help businesses build processes, improve profitability, manage risks, and create finance functions that support growth.";
  const portrait = about.portrait_url || "https://images.pexels.com/photos/6918658/pexels-photo-6918658.jpeg";
  return (
    <>
      <section className="bg-[var(--pta-cream)] pt-16 md:pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="chapter-num">About · The Founder</div>
            <h1 className="font-serif-display text-6xl md:text-8xl text-[var(--pta-navy)] mt-4 leading-[0.95]">Pankaj Talan.</h1>
            <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-[var(--pta-gold)]">Chartered Accountant · SRCC alumnus · corporate finance leader.</p>
          </div>
          <div className="lg:col-span-4">
            <div className="clip-tab border hairline-navy overflow-hidden">
              <img src={portrait} alt="Pankaj Talan" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[var(--pta-cream)] pb-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 space-y-6 text-lg leading-relaxed text-[var(--pta-ink)]/80 font-serif-display">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          <p className="text-[var(--pta-navy)] italic border-l-4 pl-6" style={{ borderColor: 'var(--pta-gold)' }}>{quote}</p>
        </div>
      </section>

      <section className="section-dark py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="chapter-num text-[var(--pta-gold)]">What We Believe</div>
          <h2 className="font-serif-display text-5xl md:text-7xl mt-4 text-[var(--pta-cream)] leading-[0.98]">Our philosophy.</h2>
          <div className="mt-10 border-t" style={{ borderColor: 'rgba(212,175,55,0.35)' }}>
            {[
              "Finance should enable growth, not create complexity.",
              "Strong processes reduce business risks.",
              "Compliance should be proactive, not reactive.",
              "Decisions should always be backed by accurate financial data.",
              "Every growing business deserves CFO-level financial guidance.",
            ].map((line, i) => (
              <div key={i} className="flex items-baseline gap-6 md:gap-10 py-6 border-b" style={{ borderColor: 'rgba(212,175,55,0.25)' }}>
                <span className="font-mono-fine text-[11px] tracking-[0.24em] text-[var(--pta-gold)]">0{i + 1}</span>
                <span className="font-serif-display text-2xl md:text-4xl text-[var(--pta-cream)]">{line}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--pta-cream)] py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="chapter-num">Core Expertise</div>
          <h2 className="font-serif-display text-5xl md:text-6xl mt-4 text-[var(--pta-navy)] leading-[1.02]">What we do best.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-t border-l hairline">
            {["Financial Reporting","Virtual CFO Services","SOP Design & Documentation","Internal Controls & Risk","Process Improvement","Internal & Process Audits","Startup Advisory","Business Finance","GST & Direct Tax Advisory","Due Diligence","Compliance Management","MIS & Business Analytics"].map((c,i)=> (
              <div key={i} className="border-r border-b hairline p-6 group">
                <div className="font-mono-fine text-[10px] tracking-[0.22em] text-[var(--pta-gold)]">Capability · {String(i+1).padStart(2,'0')}</div>
                <div className="font-serif-display text-xl md:text-2xl text-[var(--pta-navy)] mt-2 group-hover:italic">{c}</div>
              </div>
            ))}
          </div>
          <div className="mt-16 border-t-2 hairline-gold pt-10">
            <p className="font-serif-display italic text-3xl md:text-5xl text-[var(--pta-navy)] leading-tight max-w-4xl">"We don't just help businesses stay compliant — we help them become financially stronger, operationally efficient, and ready for sustainable growth."</p>
            <div className="mt-8"><Link data-testid="about-book" to="/book" className="btn-pta">Book a Consultation</Link></div>
          </div>
        </div>
      </section>
    </>
  );
}
